import clsx from 'clsx';
import { Activity, useState, useEffect } from 'react';

import {
  IconEye,
  IconTrash,
  IconXboxX,
  IconSearch,
  IconDevices,
  IconLockOpen,
  IconAdjustments,
  IconLayersLinked,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Input from '@/components/Input/Input';
import CustomTable from '@/components/Table/index';
import { CommonStatusConfig } from '@/constants/constant';
import { ECommonStatus } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  getDeviceList,
  blockDeviceApi,
  deleteDeviceApi,
  getDeviceModelDropdown,
} from '@/service/deviceServices';
import useCommonStore from '@/store/useCommonStore';
import { IDevice, IDeviceFilterValues } from '@/types';
import {
  pickBy,
  customPredicate,
  formatLastActive,
  formatLastActiveAt,
} from '@/utils';

import DeviceDetailsModal from './modal/DeviceDetailsModal';
import DeviceFilterModal from './modal/DeviceFilterModal';

type TDeviceListParams = {
  search?: string;
  status?: ECommonStatus;
  offset?: number;
  limit?: number;
  order?: Record<string, string>;
  role?: string;
  device_model?: string;
};

const DeviceManagement = () => {
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search?.trim(), 500);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<IDeviceFilterValues>({
    role: '',
    device_type: '',
    status: undefined,
  });
  const { loadingTable, setLoadingTable } = useCommonStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [device, setDevice] = useState<IDevice[]>([]);
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const [isOpenDetailsModal, setIsOpenDetailsModal] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>(
    undefined
  );
  const [selectedDevice, setSelectedDevice] = useState<IDevice | null>(null);
  const [isOpenBlockModal, setIsOpenBlockModal] = useState(false);
  const [isOpenRemoveModal, setIsOpenRemoveModal] = useState(false);
  const [deviceModelOptions, setDeviceModelOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const { formatText } = useFormatText();
  const pageTitle = formatText('device-management.title');

  useEffect(() => {
    document.title = pageTitle;
    setTitle(pageTitle);
    setIsBreadcrumb([{ label: pageTitle }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle]);

  useEffect(() => {
    const fetchDeviceModels = async () => {
      const response = await getDeviceModelDropdown();
      if (response?.status && response?.data) {
        setDeviceModelOptions(
          response?.data?.map((item: string) => ({
            value: item,
            label: item,
          }))
        );
      }
    };
    fetchDeviceModels();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    pageSize,
    debouncedSearch,
    sortColumn,
    sortDirection,
    filterValues,
  ]);

  const handleSort = (key: string) => {
    if (sortColumn === key) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else if (sortDirection === 'DESC') {
        setSortColumn('');
        setSortDirection('');
      } else {
        setSortDirection('ASC');
      }
    } else {
      setSortColumn(key);
      setSortDirection('ASC');
    }
  };

  const columns = [
    {
      title: 'device-management.column.device-id',
      key: 'device_id',
      width: '20%',
      sorter: true,
      handleSort: () => handleSort('device_id'),
      render: (row: IDevice) => (
        // HERE TWO NESTED DIV WANT NOT REMOVE AFTER DEVICE TYPE GOT IT WILL USE LIKE NOT RECOGNICE DEVICE TYPE
        <div className="flex items-center gap-2">
          <div className="min-w-10 h-10 rounded-lg border border-neutral-light-200 flex items-center justify-center">
            <IconDevices color="#717784" />
          </div>
          <span className="font-medium text-neutral-primary whitespace-nowrap">
            {row?.device_id || '-'}
          </span>
        </div>
      ),
    },
    {
      title: 'device-management.column.child-name',
      key: 'child_name',
      width: '15%',
      sorter: true,
      handleSort: () => handleSort('child_name'),
      render: (row: IDevice) =>
        row?.child_name ? (
          <div className="flex items-center gap-2">
            <AvatarGroupList
              list={[
                {
                  id: row?.id,
                  name: row?.child_name,
                  image: row?.profile_pic ?? '',
                },
              ]}
              size={40}
              avatarClassName="w-8 h-8 text-[10px]"
            />
            <span className="text-sm font-medium text-neutral-primary">
              {row?.child_name}
            </span>
          </div>
        ) : (
          '-'
        ),
    },
    {
      title: 'device-management.column.associated-parent',
      key: 'associated_parent',
      width: '15%',
      sorter: true,
      handleSort: () => handleSort('associated_parent'),
      render: (row: IDevice) => row?.associated_parent ?? '-',
    },
    {
      title: 'device-management.column.school',
      key: 'school',
      width: '15%',
      sorter: true,
      handleSort: () => handleSort('school'),
      render: (row: IDevice) => row?.school ?? '-',
    },
    {
      title: 'device-management.column.device-type',
      key: 'device_model',
      width: '15%',
      sorter: true,
      handleSort: () => handleSort('device_model'),
      render: (row: IDevice) => row?.device_model ?? '-',
    },
    {
      title: 'device-management.column.status',
      key: 'status',
      width: '15%',
      sorter: true,
      handleSort: () => handleSort('status'),
      render: (row: IDevice) => {
        const config =
          row?.status !== undefined
            ? CommonStatusConfig[row?.status as keyof typeof CommonStatusConfig]
            : undefined;
        return (
          <span
            className={`px-2 py-0.5 text-xs font-semibold rounded-full leading-6 capitalize ${
              config?.className
            }`}
          >
            {row.status !== undefined && formatText(config?.label ?? '')}
          </span>
        );
      },
    },
    {
      title: 'device-management.column.last-update',
      key: 'last_update',
      width: '20%',
      sorter: true,
      handleSort: () => handleSort('last_update'),
      render: (row: IDevice) =>
        row?.last_update ? formatLastActive(row?.last_update, formatText) : '-',
    },
    {
      title: 'device-management.column.linked-since',
      key: 'linked_since',
      width: '20%',
      sorter: true,
      handleSort: () => handleSort('linked_since'),
      render: (row: IDevice) =>
        row?.linked_since ? formatLastActiveAt(row?.linked_since) : '-',
    },
    {
      title: 'common.actions.action',
      key: 'action',
      width: '5%',
      align: 'center',
      render: (row: IDevice) => {
        const isBlocked = row.is_block;
        const actions = [
          {
            key: 'view',
            label: 'common.actions.view',
            icon: <IconEye size={18} />,
            onClick: () => {
              setSelectedDeviceId(row.id);
              setSelectedDevice(row);
              setIsOpenDetailsModal(true);
            },
          },
          {
            key: isBlocked ? 'unblock' : 'block',
            label: isBlocked
              ? 'common.actions.unblock'
              : 'common.actions.block',
            icon: isBlocked ? (
              <IconLockOpen size={18} />
            ) : (
              <IconXboxX size={18} />
            ),
            onClick: () => {
              if (isBlocked) {
                handleBlockDevice(row);
              } else {
                setSelectedDevice(row);
                setIsOpenBlockModal(true);
              }
            },
          },
          {
            key: 'remove',
            label: 'common.actions.remove',
            icon: <IconTrash size={18} />,
            isDanger: true,
            onClick: () => {
              setSelectedDevice(row);
              setIsOpenRemoveModal(true);
            },
          },
        ];

        return <ActionDropdown actions={actions} />;
      },
    },
  ];

  const fetchDevices = async () => {
    setLoadingTable(true);

    let params: TDeviceListParams = {
      search: debouncedSearch || undefined,
      status: filterValues?.status as ECommonStatus | undefined,
      offset: (currentPage - 1) * pageSize,
      limit: pageSize,
      order:
        sortColumn && sortDirection
          ? { [sortColumn]: sortDirection }
          : undefined,
      role: filterValues.role,
      device_model: filterValues?.device_type?.toLowerCase(),
    };

    params = pickBy(params, customPredicate) as TDeviceListParams;
    const response = await getDeviceList(params);
    if (response?.status) {
      setDevice(response?.data || []);
      setTotal(response?.total || 0);
    }

    setLoadingTable(false);
  };

  const handleBlockDevice = async (device = selectedDevice) => {
    if (!device) return;
    setLoadingTable(true);
    const payload = {
      id: device.id,
      is_block: !device.is_block,
    };
    const response = await blockDeviceApi(payload);
    if (response?.status) {
      fetchDevices();
    }
    setLoadingTable(false);
    setIsOpenBlockModal(false);
    setSelectedDevice(null);
  };

  const handleRemoveDevice = async () => {
    if (!selectedDevice) return;
    setLoadingTable(true);
    const response = await deleteDeviceApi({ id: selectedDevice.id });
    if (response?.status) {
      fetchDevices();
    }
    setLoadingTable(false);
    setIsOpenRemoveModal(false);
    setSelectedDevice(null);
  };

  const handleFilterApply = (filters: IDeviceFilterValues) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };

  const isFilterApplied =
    !!filterValues?.role ||
    !!filterValues?.device_type ||
    !!filterValues?.status;

  const showListUI =
    loadingTable ||
    device.length > 0 ||
    search.length > 0 ||
    debouncedSearch.length > 0 ||
    isFilterApplied;

  return (
    <>
      {showListUI && (
        <div className="flex flex-wrap justify-between items-center gap-2.5 mb-6 pl-1">
          <div className="flex gap-2.5">
            <Input
              name="search"
              placeholder={formatText('device-management.search-placeholder')}
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
              leftIcon={{
                icon: <IconSearch size={20} className="text-neutral-500" />,
              }}
              className="min-w-78"
              inputClassName="py-[13px]"
            />
            <div className="relative">
              <Button
                icon={<IconAdjustments size={20} />}
                label={formatText('common.actions.filter')}
                variant="outline"
                className={clsx(
                  'px-4 h-12 cursor-pointer',
                  isFilterApplied && 'border-brand-default! text-brand-default!'
                )}
                onClick={() => setIsFilterModalOpen(true)}
              />
              {isFilterApplied && (
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-6">
        <CustomTable
          isLoading={loadingTable}
          dataSource={device}
          columns={columns}
          total={total}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          isEmpty={
            !loadingTable &&
            device?.length === 0 &&
            search?.length === 0 &&
            debouncedSearch?.length === 0 &&
            !isFilterApplied
          }
          fieldName={sortColumn}
          sortDirection={sortDirection}
          emptyImgSrc={'/assets/svg/empty-device.svg'}
          emptyTitle={formatText('device-management.not-found-title')}
          emptyDescription={formatText('device-management.not-found-desc')}
          emptyButtonText={formatText('device-management.sync-device-btn')}
          emptyButtonIcon={<IconLayersLinked size={20} />}
          onEmptyBtnClick={() => fetchDevices()}
          isEmptySearch={
            device?.length === 0 &&
            (search?.length > 0 ||
              debouncedSearch?.length > 0 ||
              isFilterApplied)
          }
          rootClassName="!h-[calc(100vh-220px)]"
          onRowClick={(id) => {
            const row = device.find((item) => item.id === id);
            if (row) {
              setSelectedDeviceId(row.id);
              setSelectedDevice(row);
              setIsOpenDetailsModal(true);
            }
          }}
        />
      </div>

      <Activity mode={isFilterModalOpen ? 'visible' : 'hidden'}>
        <DeviceFilterModal
          setIsOpen={setIsFilterModalOpen}
          onApply={handleFilterApply}
          initialValues={filterValues}
          deviceModelOptions={deviceModelOptions}
        />
      </Activity>

      <Activity mode={isOpenDetailsModal ? 'visible' : 'hidden'}>
        <DeviceDetailsModal
          setIsOpen={setIsOpenDetailsModal}
          deviceId={selectedDeviceId}
          onRefresh={fetchDevices}
          handleRemoveDevice={() => {
            setIsOpenRemoveModal(true);
          }}
        />
      </Activity>

      {isOpenBlockModal && (
        <ConfirmationModal
          isOpen={isOpenBlockModal}
          setIsOpen={setIsOpenBlockModal}
          title="device-management.actions.block-device-title"
          descriptionPrefix="device-management.actions.block-device-prefix"
          descriptionSuffix="device-management.actions.block-device-suffix"
          entityName={selectedDevice?.device_id}
          confirmLabel={formatText('device-management.actions.block-device')}
          onConfirm={handleBlockDevice}
          isLoading={loadingTable}
          isDestructive
        />
      )}

      {isOpenRemoveModal && (
        <ConfirmationModal
          isOpen={isOpenRemoveModal}
          setIsOpen={setIsOpenRemoveModal}
          title="device-management.actions.remove-device-title"
          descriptionPrefix="device-management.actions.remove-device-prefix"
          descriptionSuffix="device-management.actions.remove-device-suffix"
          entityName={selectedDevice?.device_id}
          confirmLabel={formatText('device-management.actions.remove-device')}
          onConfirm={handleRemoveDevice}
          isLoading={loadingTable}
          isDestructive
        />
      )}
    </>
  );
};

export default DeviceManagement;
